import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { ecosystemHandoffs, ecosystemNodes } from "../../data/hrTechnology";
import { Exhibit } from "../Exhibit";
import { SectionLabel } from "../SectionLabel";
import { ledgerEase } from "../../utils/motion";
export function SystemEcosystem() {
  const [active, setActive] = useState(1);
  const handoff = ecosystemHandoffs[active];
  const nameOf = (id: string) => ecosystemNodes.find(n => n.id === id)?.name ?? id;
  const details = [{
    title: "What crosses",
    body: handoff.moves
  }, {
    title: "Where it breaks",
    body: handoff.breaks
  }, {
    title: "What we check",
    body: handoff.check
  }];
  return <section aria-labelledby="ecosystem-title" className="border-t border-stone" data-id="element-249" data-mp="i5k1c6oi1aej">
      <div className="shell py-24 md:py-32" data-id="element-250" data-mp="2oy2xep4haxi0">
        <div className="grid grid-cols-12 gap-x-6 gap-y-8 pb-14" data-id="element-251" data-mp="2jz9kaxt3t8id">
          <div className="col-span-12 lg:col-span-3 lg:pt-3" data-id="element-252" data-mp="2k0uc4fo1adcj">
            <SectionLabel data-id="element-253" data-mp="mg6tbrbqvc9o">How it connects</SectionLabel>
          </div>
          <div className="col-span-12 lg:col-span-9" data-id="element-254" data-mp="rdffs43xe2gq">
            <h2 id="ecosystem-title" className="font-display text-[clamp(2.25rem,4.4vw,3.75rem)] leading-[1.05] text-navy" data-id="element-255" data-mp="rbuja8y1w9g8">
              Five systems, four handoffs — and payroll depends on every one.
            </h2>
            <p className="mt-6 max-w-xl leading-relaxed text-charcoal/75" data-id="element-256" data-mp="155dhhtvb4w2v">
              We map the whole chain before we change any part of it. Select a handoff to see what crosses it, where it
              tends to break, and what we check.
            </p>
          </div>
        </div>

        <Exhibit label="Exhibit — System ecosystem" title="How an employee record becomes a paycheck — and a journal entry" source="Simplified view. Recruiting (ATS) typically feeds new hires into the HRIS, and payroll results also post directly to the general ledger." data-id="element-257" data-mp="1f1ouyn6mvryw">
          <ol className="flex flex-col xl:flex-row xl:items-stretch" data-id="element-258" data-mp="3dtbazay7t3v2">
            {ecosystemNodes.map((node, i) => {
            const link = ecosystemHandoffs[i];
            const isPayroll = node.id === "payroll";
            const isActive = i === active;
            return <React.Fragment key={node.id} data-id="element-259" data-mp="3np4mqs440tbs" data-mpi={i}>
                  <li className={`border p-5 xl:w-[172px] xl:flex-none ${isPayroll ? "border-navy bg-navy text-ivory" : "border-navy/70 bg-ivory text-navy"}`} data-id="element-260" data-mp="3ircqxn0cgn2b">
                    <span className={`text-[11px] tabular-nums ${isPayroll ? "text-ivory/60" : "text-charcoal/55"}`} data-id="element-261" data-mp="3rr88qaoengbb">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="mt-3 font-display text-[1.6rem] leading-none" data-id="element-262" data-mp="5fu9b0echmmu">{node.name}</p>
                    <p className={`mt-3 text-xs leading-relaxed ${isPayroll ? "text-ivory/75" : "text-charcoal/70"}`} data-id="element-263" data-mp="jb6bfsutyfds">
                      {node.detail}
                    </p>
                    {node.note && <p className="eyebrow mt-4 text-[10px] text-oxblood" data-id="element-264" data-mp="xxvc3y0tiylr">{node.note}</p>}
                  </li>

                  {link && <li className="flex xl:min-w-0 xl:flex-1" data-id="element-265" data-mp="ozt5n2ybfdr3">
                      <button type="button" aria-pressed={isActive} onClick={() => setActive(i)} className={`group flex w-full items-center gap-4 py-1 pl-8 text-left xl:flex-col xl:justify-center xl:gap-2.5 xl:px-3 xl:py-0 xl:text-center ${isActive ? "text-oxblood" : "text-navy/50 hover:text-navy"}`} data-id="element-266" data-mp="p1mcai8xhxz3">
                        <span aria-hidden className={`relative block h-12 shrink-0 bg-current xl:w-full ${isActive ? "w-0.5 xl:h-0.5" : "w-px xl:h-px"}`} data-id="element-267" data-mp="aax29j77eowt">
                          <ChevronDownIcon className="absolute -bottom-2 left-1/2 h-3.5 w-3.5 -translate-x-1/2 xl:hidden" strokeWidth={1.5} data-id="element-268" data-mp="13ltrhonrjwko" />
                          <ChevronRightIcon className="absolute -right-2 top-1/2 hidden h-3.5 w-3.5 -translate-y-1/2 xl:block" strokeWidth={1.5} data-id="element-269" data-mp="uutekubwcv90" />
                        </span>
                        <span className={`text-[11px] font-semibold uppercase leading-snug tracking-[0.12em] transition-colors duration-150 xl:order-first ${isActive ? "text-oxblood" : "text-charcoal/60 group-hover:text-navy"}`} data-id="element-270" data-mp="pvgjjmavghvc">
                          {link.label}
                        </span>
                      </button>
                    </li>}
                </React.Fragment>;
          })}
          </ol>

          <div aria-live="polite" className="mt-12 border-t border-stone pt-8" data-id="element-271" data-mp="21alybooa26vb">
            <AnimatePresence mode="wait" initial={false} data-id="element-272" data-mp="28uo8m292a0ul">
              <motion.div key={active} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease: ledgerEase
            }} className="grid grid-cols-12 gap-x-6 gap-y-8" data-id="element-273" data-mp="28u4pii7od6ez">
                <div className="col-span-12 lg:col-span-3" data-id="element-274" data-mp="1pvsboj5tj8d2">
                  <p className="eyebrow text-oxblood" data-id="element-275" data-mp="1pxd7ec3vcxci">Handoff {String(active + 1).padStart(2, "0")}</p>
                  <p className="mt-3 font-display text-2xl leading-snug text-navy" data-id="element-276" data-mp="1uuvia81g5mn5">
                    {nameOf(handoff.from)} → {nameOf(handoff.to)}
                  </p>
                </div>
                {details.map(d => <div key={d.title} className="col-span-12 md:col-span-4 lg:col-span-3" data-id="element-277" data-mp="3b957139lpdei">
                    <p className="eyebrow text-charcoal/60" data-id="element-278" data-mp="3fagoglibmsbm">{d.title}</p>
                    <p className="mt-3 text-[15px] leading-relaxed text-charcoal/85" data-id="element-279" data-mp="3f8ni77bmea45">{d.body}</p>
                  </div>)}
              </motion.div>
            </AnimatePresence>
          </div>
        </Exhibit>
      </div>
    </section>;
}